import { formatSpendingTrendCategoryAxisLabel } from 'containers/Spending/utils/spendingTrendFormat';
import type { EChartsOption } from 'echarts';
import type { ChartDataItem } from '../types';
import { buildAnimationConfig } from '../shared/animation';
import { buildCategoryAxis, buildValueAxis } from '../shared/axis';
import { buildGridConfig } from '../shared/grid';
import { buildLegendConfig } from '../shared/legend';
import { buildTooltipConfig } from '../shared/tooltip';
import { hasOnlyZeroValues } from '../shared/valueAxis';
import { buildSpendingTrendSeries } from './series';
import { buildSpendingTrendTooltipFormatter } from './tooltip';
import type { SpendingTrendBarChartConfig } from './types';

const DEFAULT_MAX_Y_AXIS_LABELS = 4;

export const buildSpendingTrendBarChartOption = (
  data: ChartDataItem[],
  orderedFields: string[],
  config: SpendingTrendBarChartConfig,
): EChartsOption => {
  const {
    theme,
    withLegend = false,
    animated = true,
    categoryField = 'category',
    colorOverrides,
    maxYAxisLabels = DEFAULT_MAX_Y_AXIS_LABELS,
    enableQuarterLabelRemap = false,
    categoryTimestep,
  } = config;

  // Single pass — labelOverrides[i] only depends on categories[i].
  const lastCategoryLabel = data.length ? String(data[data.length - 1]?.category ?? '') : null;
  const categories: string[] = new Array(data.length);
  const labelOverrides: string[] = new Array(data.length);
  data.forEach((d, i) => {
    const category = String(d[categoryField] ?? '');
    categories[i] = category;
    labelOverrides[i] = formatSpendingTrendCategoryAxisLabel(
      category,
      lastCategoryLabel,
      enableQuarterLabelRemap,
      category,
      categoryTimestep,
    );
  });

  // ECharts otherwise creates fractional ticks for an all-zero series, which all format as "$0"
  // at this axis's display precision and repeat down the axis in empty dashboard states.
  const hasZeroOnlyValues = hasOnlyZeroValues(data, orderedFields);

  return {
    ...buildAnimationConfig(animated),
    textStyle: { fontFamily: theme.fontFamily },
    color: colorOverrides ?? theme.colors,
    legend: buildLegendConfig(withLegend, theme),
    grid: buildGridConfig({ withLegend }),
    tooltip: {
      ...buildTooltipConfig(theme),
      trigger: 'axis' as const,
      // Custom floating card owns its own chrome; zero out the native bordered shell.
      borderWidth: 0,
      padding: 0,
      backgroundColor: 'transparent',
      extraCssText: 'box-shadow:none;',
      formatter: buildSpendingTrendTooltipFormatter(data, orderedFields, config),
    },
    xAxis: buildCategoryAxis(categories, theme, undefined, false, labelOverrides),
    yAxis: buildValueAxis({
      theme,
      formatAmount: config.formatAmount,
      privacyMask: config.privacyMask,
      showGridLines: true,
      useNumberPrefix: true,
      splitNumber: Math.max(1, maxYAxisLabels - 1),
      ...(hasZeroOnlyValues && { min: 0, max: 1, splitNumber: 1 }),
    }),
    series: buildSpendingTrendSeries(data, orderedFields, categoryField, config),
  };
};
